/**
 * SessionContext - Owner Portal Authentication & Bootstrap Context
 *
 * Purpose:
 * Provides centralized session management, authentication state, and property selection for the Owner Portal.
 * Handles admin/owner mode detection, properties fetching with retry logic, property selection persistence,
 * and access scoping. This is the primary auth/bootstrap path for the Owner Portal and is consumed by 20+
 * owner components via the useSession hook.
 *
 * High-level Workflow:
 * 1. On mount: Detect portal type (admin vs owner) and log portal detection
 * 2. Admin detection: Check query param override (?admin=1/0) or call backend auth endpoint to determine admin status
 * 3. Properties fetch: Fetch properties from API (portal-aware: /api/v1/owner/properties or /api/v1/admin/properties)
 *    - Retry on auth errors (401/403) up to 3 times with increasing delays
 *    - Retry on transient errors (502/503/504/network) up to 5 times with increasing delays
 *    - Skip fetch on unauthenticated paths (/login, /owner/reset-password, /forgot-password)
 * 4. Property selection: Initialize selectedListingId from localStorage or default to first available property
 *    - Persist selection to localStorage (owner_selected_listing_id)
 *    - Migrate legacy localStorage key (selectedPropertyId → owner_selected_listing_id)
 *    - Validate selection exists in available properties, clear invalid selections
 * 5. Access scoping: Compute accessible properties based on effective mode (owner/owner_view/admin)
 * 6. Debug hooks: Attach window.__OWNER_DEBUG__ hook for Playwright/testing when ?debug=1
 *
 * Assumptions & Invariants:
 * - Portal detection is hostname-based (admin.* or owner/clcvacation domains)
 * - Admin auth service is dynamically imported (not bundled in owner portal)
 * - Properties are pre-filtered by backend (owner receives only accessible properties)
 * - selectedListingId is the canonical identifier (numeric, persisted as string in localStorage)
 * - Query param override (?admin=1/0) takes highest precedence for admin status
 * - Effective mode: "owner" (pure owner), "owner_view" (admin viewing as owner), "admin" (full admin)
 * - No mock data: all data comes from live API calls after authentication
 * - Properties fetch retries are bounded (max 3 auth retries, max 5 transient retries)
 * - localStorage persistence is conditional (admin state only persisted if from query param)
 *
 * Dependencies:
 * Internal:
 *   - hooks/useAdminStatus.js: Admin status detection and management (Phase 3)
 *   - hooks/usePropertiesFetch.js: Properties fetching with retry logic (Phase 3)
 *   - hooks/usePropertySelection.js: Property selection management (Phase 3)
 *   - utils/portalDetection.js: logPortalDetection()
 * External:
 *   - react: createContext, useContext, useEffect, useCallback, useMemo
 *   - react-router-dom: useLocation() for pathname detection
 *
 * Version: 1.3.0
 *
 * Changelog:
 * - 2026-01-26: Phase 1 - Added charter-compliant file header and intent comments for high-risk sections
 * - 2026-01-26: Phase 2 - Extracted pure utilities (shouldSkipPropertiesFetch, migrateSelectedListingId) to utils/sessionContextUtils.js
 * - 2026-01-26: Phase 3 - Extracted hooks (useAdminStatus, usePropertiesFetch, usePropertySelection) to reduce responsibility overload
 * - 2026-01-26: Phase 4 - Removed dead code (legacy admin detection functions), added log gating for console statements
 */

import { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { logPortalDetection } from '../utils/portalDetection.js';
import { useAdminStatus } from '../hooks/useAdminStatus.js';
import { usePropertiesFetch } from '../hooks/usePropertiesFetch.js';
import { usePropertySelection } from '../hooks/usePropertySelection.js';
// Use dynamic import for admin auth to prevent it from loading in owner bundle
// This prevents "[ADMIN AUTH]" logs from appearing on owner portal

/**
 * SessionContext - Hardened admin/owner separation
 * 
 * Effective modes:
 * - "owner": Pure owner mode (no admin UI, no admin data)
 * - "owner_view": Admin viewing as owner (admin UI visible, but data is owner-safe)
 * - "admin": Admin mode with full access (admin UI + admin data)
 */
const SessionContext = createContext(null);

export function SessionProvider({ children, initialAuthMe = null }) {
  // Log portal detection at startup
  useEffect(() => {
    logPortalDetection();
  }, []);
  
  // Debug mode (only when ?debug=1)
  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  // Helper to check if debug logging is enabled
  const shouldLog = () => {
    if (typeof window === 'undefined') return false;
    return process.env.NODE_ENV === 'development' ||
           new URLSearchParams(window.location.search).get('debug') === '1';
  };

  const location = useLocation();
  const pathname = location?.pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');

  // Extract hooks (Phase 3) - CRITICAL: Must be called before useMemo/useCallback that reference their values
  const {
    isAdminUser,
    viewAsOwner,
    setViewAsOwner,
    effectiveMode,
    adminInsightsEnabled,
    setAdminInsightsEnabled,
    selectedOwnerId,
    setSelectedOwnerId,
    currentUser,
    debugInfo,
  } = useAdminStatus(pathname, { initialAuthMe });

  const {
    properties,
    propertiesLoading,
    propertiesError,
    retryProperties,
  } = usePropertiesFetch(pathname);

  // CRITICAL: NO mock data - all data must come from live API calls after authentication
  // Initialize with empty arrays - will be populated from API

  // Helper functions for defaults (no mock fallback)
  const getDefaultOwnerId = useCallback(() => {
    // No default - will be set from API after authentication
    return null;
  }, []);

  const getDefaultPropertyId = useCallback((ownerId, accessiblePropertyIds = null) => {
    // Return first accessible property if provided, otherwise null
    if (accessiblePropertyIds && accessiblePropertyIds.length > 0) {
      return accessiblePropertyIds[0];
    }
    return null;
  }, []);

  // Check for query param override (highest precedence ALWAYS)
  const getQueryParamOverride = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    const adminParam = urlParams.get('admin');
    if (adminParam === '1') return { value: true, source: 'query' };
    if (adminParam === '0') return { value: false, source: 'query' };
    return null;
  }, []);

  /**
   * detectAdminFromBackend - Portal-aware admin status detection
   * 
   * Intent: Detects admin status by calling the appropriate auth endpoint based on detected portal.
   * Uses dynamic import for admin auth service to prevent bundling in owner portal. Handles both
   * admin and owner portals with different detection logic (admin: isSuperAdmin/capabilities,
   * owner: role-based check for superadmin/operator/admin roles).
   * 
   * Risk: No AbortController - request continues even if component unmounts. No mount check before
   * setState in calling code (determineAdminStatus useEffect). Consider adding AbortController and
   * isMountedRef pattern in Phase 4.
   * 
   * @returns {Promise<{isAdmin: boolean, debugInfo: object}>} Admin status and debug information
   */
  // Auto-detect admin status from backend (non-blocking, never blocks render)
  // PORTAL-AWARE: Use correct endpoint based on detected portal
  const detectAdminFromBackend = useCallback(async () => {
    const portal = detectPortal();
    const apiBase = getApiBase();
    const endpoint = portal === 'admin' ? '/api/v1/admin/auth/me' : '/api/v1/owner/auth/me';
    
    let debugInfo = {
      status: 'loading',
      httpStatus: null,
      errorMessage: null,
      adminSource: 'backend',
      endpoint,
      portal,
    };

    try {
      // Portal-aware: use admin endpoint on admin domain, owner endpoint on owner domain
      let response;
      let data;
      
      if (portal === 'admin') {
        // Admin portal: use admin auth service (dynamic import to avoid bundling in owner portal)
        const { getCurrentAdmin } = await import('../services/adminAuthService.js');
        const adminUser = await getCurrentAdmin();
        if (adminUser) {
          // Admin user is authenticated
          const isAdmin = adminUser.isSuperAdmin || (adminUser.capabilities && adminUser.capabilities.length > 0);
          debugInfo.status = 'success';
          debugInfo.httpStatus = 200;
          debugInfo.isAdmin = isAdmin;
          return { isAdmin, debugInfo };
        } else {
          // Not authenticated
          debugInfo.status = 'unauthorized';
          debugInfo.httpStatus = 401;
          debugInfo.errorMessage = 'Not authenticated';
          return { isAdmin: false, debugInfo };
        }
      } else {
        // Owner portal: use owner auth endpoint (uses owner.sid cookie)
        response = await fetch('/api/v1/owner/auth/me', {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });

        debugInfo.httpStatus = response.status;

        if (response.ok) {
          data = await response.json();
          // Check for admin status from user roles (shared RBAC)
          // Admin roles: superadmin, operator, admin
          const userRoles = data.data?.user?.roles || data.data?.roles || data.roles || [];
          const isAdmin = userRoles.some(role => ['superadmin', 'operator', 'admin'].includes(role));
          debugInfo.status = 'success';
          debugInfo.isAdmin = isAdmin;
          return { isAdmin, debugInfo };
        } else {
          debugInfo.status = response.status === 401 || response.status === 403 ? 'unauthorized' : 'error';
          debugInfo.errorMessage = `HTTP ${response.status}`;
          return { isAdmin: false, debugInfo };
        }
      }
    } catch (err) {
      debugInfo.status = 'error';
      debugInfo.errorMessage = err.message || 'Network error';
      return { isAdmin: false, debugInfo };
    }
  }, []);

  /**
   * determineAdminStatus - Deterministic admin status resolution
   * 
   * Intent: Resolves admin status using deterministic rules with clear precedence. Query param
   * override (?admin=1/0) takes highest precedence, then falls back to backend detection.
   * This ensures predictable behavior and allows testing/debugging via query params.
   * 
   * Precedence:
   * 1. Query param override (?admin=1/0) - highest precedence
   * 2. Backend detection (detectAdminFromBackend) - primary path
   * 
   * @returns {Promise<{isAdmin: boolean, source: string, debugInfo: object}>} Admin status with source and debug info
   */
  // Determine admin enablement (deterministic rules)
  // CRITICAL: NO mock mode - always use backend detection
  const determineAdminStatus = useCallback(async () => {
    // Rule 1: Query override takes highest precedence ALWAYS
    const queryOverride = getQueryParamOverride();
    if (queryOverride) {
      return {
        isAdmin: queryOverride.value,
        source: 'query',
        debugInfo: { status: 'success', adminSource: 'query', httpStatus: null, errorMessage: null },
      };
    }

    // Rule 2: Use backend detection (primary path - NO mock mode)
    const { isAdmin: detectedAdmin, debugInfo: backendDebugInfo } = await detectAdminFromBackend();
    return {
      isAdmin: detectedAdmin,
      source: 'backend',
      debugInfo: backendDebugInfo,
    };
  }, [getQueryParamOverride, detectAdminFromBackend]);

  // Admin and properties state now come from hooks (Phase 3)

  // Property selection state now comes from usePropertySelection hook (Phase 3)

  // Get current owner identity for access scoping (from authenticated user)
  const getCurrentOwnerId = useCallback(() => {
    // No default - will be determined from authenticated user/owner
    return null;
  }, []);

  // Get accessible properties based on effective mode and owner access
  const getAccessibleProperties = useCallback((mode, ownerId, propsList) => {
    // CRITICAL: NO mock fallback - use live properties only
    if (!propsList || propsList.length === 0) {
      return [];
    }
    
    if (mode === 'admin' || mode === 'owner_view') {
      // Admin/owner_view: see all properties
      return propsList;
    } else if (mode === 'owner') {
      // Owner: see only assigned properties (filtered by owner_id from backend)
      return propsList;
    }
    // Fallback: return empty array
    return [];
  }, []);

  // Current owner identity for access scoping (computed from effective mode)
  const currentOwnerId = effectiveMode === 'owner' ? getCurrentOwnerId() : selectedOwnerId;

  // Get available properties for current user (access-scoped) - MEMOIZED to prevent effect churn
  const availableProperties = useMemo(() => {
    return getAccessibleProperties(effectiveMode, currentOwnerId, properties);
  }, [effectiveMode, currentOwnerId, properties, getAccessibleProperties]);

  // Extract property selection hook (Phase 3)
  const {
    selectedListingId: selectedListingIdState,
    setSelectedListingId,
    selectedProperty,
    lastSelectionSource,
    lastSelectionAt,
    providerInstanceId,
  } = usePropertySelection(properties, availableProperties, propertiesLoading, debug);

  /**
   * Debug Hook Effect - Attach window.__OWNER_DEBUG__ for Playwright/testing
   * 
   * Intent: Attaches a debug hook to window.__OWNER_DEBUG__ when ?debug=1 query param is present.
   * Provides programmatic access to session state for automated testing (Playwright). Allows
   * getting current state, setting listing ID, and clearing localStorage.
   * 
   * Debug Hook API:
   * - getState(): Returns current selection state (selectedListingId, selectedProperty, availableProperties, etc.)
   * - setListingId(listingId, source): Sets selectedListingId programmatically (for testing)
   * - clearStorage(): Clears localStorage keys (owner_selected_listing_id, selectedPropertyId)
   * 
   * Risk: Hook is recreated on every dependency change. Consider using useRef for hook storage
   * to reduce re-creation. Dependencies include selectedListingIdState, selectedProperty, availableProperties,
   * which may change frequently.
   * 
   * Dependencies: [selectedListingIdState, selectedProperty, availableProperties, providerInstanceId, lastSelectionSource, lastSelectionAt, debug, setSelectedListingId]
   */
  // PHASE 1: Debug-only test hook (dev only, ?debug=1)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const isDebugMode = new URLSearchParams(window.location.search).get('debug') === '1';
    // CRITICAL: NO hostname-based dev behavior - always use live APIs
    
    if (!isDebugMode) {
      // Clean up hook if it exists but conditions no longer met
      if (window.__OWNER_DEBUG__) {
        delete window.__OWNER_DEBUG__;
      }
      return;
    }
    
    // Create/update debug hook
    window.__OWNER_DEBUG__ = {
      getState: () => {
        const prop = selectedProperty || null;
        return {
          selectedListingId: selectedListingIdState,
          selectedProperty: prop ? {
            listing_id: prop.listing_id,
            name: prop.name || prop.property_name || null,
          } : null,
          availableProperties: availableProperties.map(p => ({
            listing_id: p.listing_id,
            name: p.name || p.property_name || null,
          })),
          providerInstanceId,
          lastSelectionSource,
          lastSelectionAt,
        };
      },
      setListingId: (listingId, source = 'playwright') => {
        const oldId = selectedListingIdState;
        const newId = Number(listingId);
        if (!Number.isFinite(newId) || newId <= 0) {
          if (shouldLog()) {
            console.error('[OWNER_SELECT][HOOK] Invalid listingId:', listingId);
          }
          return;
        }
        if (debug && shouldLog()) {
          console.log('[OWNER_SELECT][HOOK] setListingId:', { oldId, newId, source });
        }
        setSelectedListingId(newId, source);
      },
      clearStorage: () => {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('owner_selected_listing_id');
          localStorage.removeItem('selectedPropertyId'); // Legacy key
          if (debug && shouldLog()) {
            console.log('[OWNER_SELECT][HOOK] Cleared localStorage');
          }
        }
      },
    };
    
    if (debug && shouldLog()) {
      console.log('[OWNER_SELECT][HOOK] Debug hook attached to window.__OWNER_DEBUG__');
    }
  }, [selectedListingIdState, selectedProperty, availableProperties, providerInstanceId, lastSelectionSource, lastSelectionAt, debug, setSelectedListingId]);

  const value = {
    // Core state
    isAdminUser,
    effectiveMode, // "owner" | "owner_view" | "admin"
    adminInsightsEnabled,
    setAdminInsightsEnabled,
    selectedOwnerId,
    setSelectedOwnerId,
    selectedListingId: selectedListingIdState,
    setSelectedListingId,
    // PHASE 1: Debug state for property selection
    lastSelectionSource,
    lastSelectionAt,
    providerInstanceId,
    // Backward compatibility: selectedPropertyId maps to selectedListingId (string)
    selectedPropertyId: selectedListingIdState ? String(selectedListingIdState) : null,
    setSelectedPropertyId: (id) => {
      const numId = typeof id === 'string' ? parseInt(id, 10) : id;
      if (!isNaN(numId) && numId > 0) {
        setSelectedListingId(numId, 'user_click'); // Backward compat - treat as user click
      } else {
        if (shouldLog()) {
          console.warn('[OWNER_SELECT] [SessionContext] Invalid listing_id provided to setSelectedPropertyId:', id);
        }
      }
    },
    selectedProperty, // Property object { id, name, ownerId, city }
    availableProperties, // Access-scoped properties user can access
    properties, // Live properties from API
    propertiesLoading, // Loading state for properties
    propertiesError, // Error state for properties fetch
    retryProperties, // Retry properties fetch (no full refresh)
    currentUser, // Authenticated owner/admin user from /owner/auth/me
    currentOwnerId, // Current owner identity for access scoping (mock placeholder)
    viewAsOwner,
    setViewAsOwner,
    debugInfo,
    // Helpers
    isOwnerMode: effectiveMode === 'owner',
    isOwnerViewMode: effectiveMode === 'owner_view',
    isAdminMode: effectiveMode === 'admin',
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
